import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/utils/dateUtils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, AlertCircle, Trash2, Loader2 } from "lucide-react";
import { useDoctors } from "@/hooks/useDoctors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { PermissionGuard } from "@/components/auth/RoleGuard";
import { showSuccess, showError } from "@/utils/toastHelpers";

export default function DoctorSelectionForPracticeOwners({
  selectedService,
  selectedDate,
  allSlots,
  doctorFilter,
  loadingSlots,
  onDoctorFilterChange,
  practiceId = null, // Allow specifying practice ID for patients
}) {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS

  // Fetch all doctors for the practice (or specified practice for patients)
  const { data: allDoctors = [], isLoading: doctorsLoading } =
    useDoctors(practiceId);

  const queryClient = useQueryClient();

  // Create invite mutation
  const inviteDoctorMutation = useMutation({
    mutationFn: (doctorId) => apiClient.post(`/api/doctors/${doctorId}/invite`),
    onSuccess: () => {
      queryClient.invalidateQueries(["doctors"]);
    },
  });

  // Create remove doctor mutation
  const removeDoctorMutation = useMutation({
    mutationFn: (doctorId) =>
      apiClient.delete(`/api/users/doctors/${doctorId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["doctors"]);
      showSuccess("Doctor removed successfully");
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.error ||
        "Failed to remove doctor. Please try again.";
      showError("Failed to remove doctor", errorMessage);
    },
  });

  // Calculate available doctors - this useMemo must be called before early returns
  const { availableDoctors, invitedDoctorsWithSlots } = useMemo(() => {
    if (!allSlots || allSlots.length === 0) {
      return { availableDoctors: [], invitedDoctorsWithSlots: [] };
    }

    // Get unique doctor IDs from available slots
    const doctorIdsWithSlots = Array.from(
      new Set(
        allSlots.filter((slot) => slot.doctorId).map((slot) => slot.doctorId)
      )
    );

    // Filter active doctors with available slots
    const activeDoctors = allDoctors
      .filter(
        (doctor) =>
          doctor.status === "active" && doctorIdsWithSlots.includes(doctor.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    // Find invited doctors with available slots
    const invitedDoctors = allDoctors
      .filter(
        (doctor) =>
          doctor.status === "invited" && doctorIdsWithSlots.includes(doctor.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      availableDoctors: activeDoctors,
      invitedDoctorsWithSlots: invitedDoctors,
    };
  }, [allDoctors, allSlots]);

  const handleInviteDoctor = (doctorId) => {
    inviteDoctorMutation.mutate(doctorId);
  };

  const handleRemoveDoctor = (doctor) => {
    const confirmMessage = `Are you sure you want to remove ${doctor.name} from the practice? This action cannot be undone.`;
    if (window.confirm(confirmMessage)) {
      removeDoctorMutation.mutate(doctor.id);
    }
  };

  // Only show doctor dropdown when service requires doctor and date is selected
  const serviceRequiresDoctor = selectedService?.requires_doctor;
  const hasSelectedDate = !!selectedDate;

  if (!serviceRequiresDoctor || !hasSelectedDate) {
    return null;
  }

  if (doctorsLoading) {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium">Select Doctor *</Label>
        <div className="w-full h-10 bg-gray-100 animate-pulse rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Label className="text-sm font-medium">Select Doctor *</Label>

      {loadingSlots ? (
        <div className="w-full p-3 border border-slate-200 rounded-md bg-slate-50 text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading available doctors...
        </div>
      ) : availableDoctors.length > 0 ? (
        <Select
          key={selectedDate ? selectedDate.toISOString() : "no-date"}
          value={doctorFilter || undefined}
          onValueChange={onDoctorFilterChange}
        >
          <SelectTrigger className="w-full" data-testid="appointment-doctor-select">
            <SelectValue placeholder="Choose a doctor" />
          </SelectTrigger>
          <SelectContent>
            {availableDoctors.map((doctor, index) => (
              <SelectItem key={doctor.id} value={doctor.id} data-testid={`appointment-doctor-option-${index}`}>
                {doctor.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <div className="w-full p-3 border border-slate-200 rounded-md bg-slate-50 text-slate-500">
          No active doctors available for this time slot
        </div>
      )}

      {/* Show invited doctors with invite buttons */}
      {invitedDoctorsWithSlots.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">
              Doctors Pending Invitation
            </span>
          </div>
          <div className="space-y-2">
            {invitedDoctorsWithSlots.map((doctor) => (
              <div
                key={doctor.id}
                className="flex flex-col items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg"
              >
                <div>
                  <div className="font-medium text-amber-900">
                    {doctor.name}
                  </div>
                  <div className="text-sm text-amber-700">{doctor.email}</div>
                  <div className="text-xs text-amber-600">
                    Has available time slots but needs to activate account
                  </div>
                  {doctor.lastInvitedAt && (
                    <div className="text-xs text-amber-600 mt-1">
                      Invitation sent: {formatDateTime(doctor.lastInvitedAt)}
                    </div>
                  )}
                </div>
                <PermissionGuard permissions={["manage_users"]}>
                  <div className="flex gap-2 p-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleInviteDoctor(doctor.id)}
                      disabled={inviteDoctorMutation.isPending}
                      className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                    >
                      <Mail className="w-4 h-4" />
                      {inviteDoctorMutation.isPending
                        ? "Sending..."
                        : doctor.lastInvitedAt
                          ? "Resend Invite"
                          : "Send Invite"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveDoctor(doctor)}
                      disabled={removeDoctorMutation.isPending}
                      className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    >
                      <Trash2 className="w-4 h-4" />
                      {removeDoctorMutation.isPending ? "Removing..." : "Remove"}
                    </Button>
                  </div>
                </PermissionGuard>
              </div>
            ))}
          </div>
        </div>
      )}

      {!doctorFilter && availableDoctors.length > 0 && (
        <p className="text-xs text-red-500">
          Please select a doctor to see available appointment times
        </p>
      )}
    </div>
  );
}
